import React, { FC, RefObject, ReactNode, useMemo } from "react";
import { OverviewType } from "../../../../app";
import { TreeSelect } from "antd";
import SelectEmptyState from "../../../../metadata/components/categorization/SelectEmptyState";
import { ColumnItem } from "../../../interfaces/overview";
import { handleKeyEvent } from "../../../utils/keyboardHelper";
import useShowBlankEntriesCheckbox from "../../../hooks/useShowBlankEntriesCheckbox";
import useFilteringSearch from "../../../hooks/useFilteringSearch";
import useApiFilterableItem from "../../../hooks/useApiFilterableItem";
import BlankEntriesCheckbox from "./BlankEntriesCheckbox";
import useTableHeaderFilter from "../../../hooks/useTableHeaderFilter";
import { getKeyFromMetaData } from "../../../utils/columnItemMetaDataHelpers";
import ExtraSelectFilter from "./ExtraSelectFilter";
import {
  buildCertificateTree,
  unescapeFilterValue,
} from "../../../utils/certificateTree";

type Props = {
  columnItem: ColumnItem;
  label: ReactNode;
  placeholder: string;
  mode?: "multiple";
  renderRef: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  overviewType: OverviewType;
};

const ApiTreeFilterableItem: FC<Props> = ({
  columnItem,
  label,
  placeholder,
  renderRef,
  isOpen,
  overviewType,
}) => {
  const { getValueFromKey } = useTableHeaderFilter(columnItem, overviewType);

  const values = useMemo(() => {
    return (
      getValueFromKey(
        getKeyFromMetaData(columnItem?.metadata, overviewType),
        columnItem?.metadata?.filterAble!.delimiter,
      )
        ?.split("+")
        // Restore any literal "+" that was escaped when the value was stored, so
        // the tree node values (which carry the raw certificate path) match again.
        .map(unescapeFilterValue)
    );
  }, [getValueFromKey, columnItem?.metadata, overviewType]);

  const { options, handlePreFetch, handleBlur, searchValue } =
    useFilteringSearch(
      columnItem?.metadata?.filterAble!.query,
      undefined,
      values,
    );

  const {
    isBlankEntriesChecked,
    handleChangeBlankEntries,
    labelCheckbox,
    showBlankEntriesCheckbox,
    blankValue,
  } = useShowBlankEntriesCheckbox(columnItem, overviewType, values);

  // The tree always behaves as a multi-select, so we reuse the filterable item
  // hook in "multiple" mode to keep the stored value plumbing identical to the
  // flat API_SELECT.
  const { ref, selectValue, handleChange, open, setOpen } =
    useApiFilterableItem(
      columnItem,
      overviewType,
      isOpen,
      isBlankEntriesChecked,
      blankValue,
      values,
      searchValue,
      () => {},
      "multiple",
    );

  const pathProperty = columnItem?.metadata?.filterAble?.query?.pathProperty;

  const treeData = useMemo(
    () => buildCertificateTree(options, pathProperty),
    [options, pathProperty],
  );

  return (
    <div className="filter-popover__item">
      <label className="filter-popover__label">{label}</label>
      {showBlankEntriesCheckbox ? (
        <BlankEntriesCheckbox
          checkboxValue={isBlankEntriesChecked}
          handleChangeBlankEntries={handleChangeBlankEntries}
          labelCheckbox={labelCheckbox}
        />
      ) : null}
      <TreeSelect
        ref={ref}
        showSearch
        allowClear
        value={selectValue}
        placeholder={placeholder}
        treeData={treeData}
        treeCheckable
        // Checking a group emits only its leaf ids, so the backend never sees a
        // synthetic group value.
        showCheckedStrategy={TreeSelect.SHOW_CHILD}
        // Full set is prefetched, so search filters the loaded tree locally.
        filterTreeNode={(input, node) =>
          String(node.title).toLowerCase().includes(input.toLowerCase())
        }
        autoClearSearchValue={false}
        maxTagCount="responsive"
        notFoundContent={<SelectEmptyState />}
        onChange={handleChange}
        onBlur={() => {
          handleBlur();
        }}
        getPopupContainer={() => renderRef.current!}
        onInputKeyDown={handleKeyEvent}
        open={open}
        onDropdownVisibleChange={(open) => setOpen(open)}
        onFocus={() => handlePreFetch()}
      />
      <ExtraSelectFilter
        columnItem={columnItem}
        overviewType={overviewType}
        isOpen={isOpen}
      />
    </div>
  );
};

export default ApiTreeFilterableItem;
